import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';
import {
  Button as StyledButton,
  Heading,
  Input
} from './PassengerTools.styles';
import SoftPullSection from './Users/SoftPullSection';
import TradeSection from './Users/TradeSection';
import {
  DEFAULT_TRADE_CONFIG,
  TradeConfig,
  getTradeVin,
  isPendingMissingVin
} from './Users/TradeSection';
import UsersList from './Users/UsersList';
import {
  CreatedUser,
  addSoftPullToCurrentUser,
  addTradeToCurrentUser,
  clearTestUsersFromLocalStorage,
  createCommonUser,
  getCurrentSessionUser,
  getTestUsersFromLocalStorage,
  loginUser,
  logoutUser
} from './utils/createTestUser';

export default function Users() {
  const [addSecondTrade, setAddSecondTrade] = useState(false);
  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [existingUsers, setExistingUsers] = useState<CreatedUser[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [name, setName] = useState('');
  const [showExistingUsers, setShowExistingUsers] = useState(false);
  const [softPullCreditStatus, setSoftPullCreditStatus] = useState<
    'Excellent' | 'Fair'
  >('Excellent');
  const [softPullExpired, setSoftPullExpired] = useState(false);
  const [softPullOpen, setSoftPullOpen] = useState(false);
  const [trade1, setTrade1] = useState<TradeConfig>({
    ...DEFAULT_TRADE_CONFIG
  });
  const [trade2, setTrade2] = useState<TradeConfig>({
    ...DEFAULT_TRADE_CONFIG
  });
  const [tradeOpen, setTradeOpen] = useState(false);

  useEffect(() => {
    const sessionUser = getCurrentSessionUser();
    setIsLoggedIn(!!sessionUser?.userId);
  }, []);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    setErrorMessage('');
    try {
      const user = await createCommonUser(name || undefined);
      setCreatedUser(user);
    } catch (error) {
      setErrorMessage(`Failed to create user: ${error}`);
    } finally {
      setIsCreating(false);
    }
  }, [name]);

  const handleCreateAndLogin = useCallback(async () => {
    setIsCreating(true);
    setErrorMessage('');
    try {
      const user = await createCommonUser(name || undefined);
      await loginUser(user);
    } catch (error) {
      setErrorMessage(`Failed to create user: ${error}`);
      setIsCreating(false);
    }
  }, [name]);

  const handleUpdateUser = useCallback(async () => {
    setIsUpdating(true);
    setErrorMessage('');
    try {
      if (tradeOpen) {
        await addTradeToCurrentUser({
          equityLevel: trade1.equityLevel,
          loanAmount: trade1.loanAmount
            ? parseInt(trade1.loanAmount, 10)
            : undefined,
          status: trade1.status,
          vin: getTradeVin(trade1)
        });
        if (addSecondTrade) {
          await addTradeToCurrentUser({
            equityLevel: trade2.equityLevel,
            loanAmount: trade2.loanAmount
              ? parseInt(trade2.loanAmount, 10)
              : undefined,
            status: trade2.status,
            vin: getTradeVin(trade2)
          });
        }
      }
      if (softPullOpen) {
        await addSoftPullToCurrentUser({
          creditStatus: softPullCreditStatus,
          expired: softPullExpired
        });
      }
      window.location.reload();
    } catch (error) {
      setErrorMessage(`Failed to update user: ${error}`);
      setIsUpdating(false);
    }
  }, [
    addSecondTrade,
    softPullCreditStatus,
    softPullExpired,
    softPullOpen,
    trade1,
    trade2,
    tradeOpen
  ]);

  const handleLoginUser = useCallback(async (user: CreatedUser) => {
    try {
      await loginUser(user);
    } catch (error) {
      setErrorMessage(`Failed to login: ${error}`);
    }
  }, []);

  const handleViewExistingUsers = useCallback(() => {
    const storedUsers = getTestUsersFromLocalStorage();
    const usersArray = Object.values(storedUsers).sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      const validDateA = isNaN(dateA) ? 0 : dateA;
      const validDateB = isNaN(dateB) ? 0 : dateB;

      return validDateB - validDateA;
    });
    setExistingUsers(usersArray);
    setShowExistingUsers(true);
  }, []);

  const handleClearUsers = useCallback(() => {
    if (window.confirm('Are you sure you want to delete all saved users?')) {
      clearTestUsersFromLocalStorage();
      setExistingUsers([]);
    }
  }, []);

  const handleUpdateUserState = useCallback((updatedUser: CreatedUser) => {
    setExistingUsers(prev =>
      prev.map(u => (u.email === updatedUser.email ? updatedUser : u))
    );
  }, []);

  const canUpdateUser =
    isLoggedIn &&
    !isUpdating &&
    (softPullOpen || tradeOpen) &&
    (!tradeOpen ||
      (!isPendingMissingVin(trade1) &&
        (!addSecondTrade || !isPendingMissingVin(trade2))));

  if (showExistingUsers) {
    return (
      <UsersList
        onBack={() => setShowExistingUsers(false)}
        onClear={handleClearUsers}
        onLogin={handleLoginUser}
        onUpdateUser={handleUpdateUserState}
        showClearButton={true}
        title="Existing Users"
        users={existingUsers}
      />
    );
  }

  if (createdUser) {
    return (
      <UsersList
        onBack={() => setCreatedUser(null)}
        onLogin={handleLoginUser}
        onUpdateUser={updatedUser => setCreatedUser(updatedUser)}
        showClearButton={false}
        title="User Created"
        users={[createdUser]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <Heading>Create Common User</Heading>

      <FormField label="User Name (Optional)" htmlFor="user-name">
        <Input
          id="user-name"
          type="text"
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          placeholder="e.g. My Test User"
        />
      </FormField>

      {errorMessage && <div className="text-[#feaaaa]">{errorMessage}</div>}

      <div className="flex flex-wrap gap-8">
        <StyledButton
          type="button"
          onClick={handleCreate}
          data-analytics-id={null}
          disabled={isCreating || isUpdating}
          data-testid="create-user"
        >
          {isCreating ? 'Creating...' : 'Create'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleCreateAndLogin}
          data-analytics-id={null}
          disabled={isCreating || isUpdating}
          data-testid="create-and-login-user"
        >
          {isCreating ? 'Creating...' : 'Create & Login'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleUpdateUser}
          data-analytics-id={null}
          disabled={!canUpdateUser}
          data-testid="update-user"
        >
          {isUpdating ? 'Updating...' : 'Update User'}
        </StyledButton>
        <StyledButton
          type="button"
          onClick={handleViewExistingUsers}
          data-analytics-id={null}
          className="ml-auto bg-gray-600 text-white hover:bg-gray-700"
          data-testid="view-existing-users"
        >
          Existing Users
        </StyledButton>
        {isLoggedIn && (
          <StyledButton
            type="button"
            onClick={logoutUser}
            data-analytics-id={null}
            className="bg-gray-600 text-white hover:bg-gray-700"
            data-testid="logout-user"
          >
            Logout
          </StyledButton>
        )}
      </div>

      <SoftPullSection
        creditStatus={softPullCreditStatus}
        expired={softPullExpired}
        isLoggedIn={isLoggedIn}
        isOpen={softPullOpen}
        onCreditStatusChange={setSoftPullCreditStatus}
        onExpiredChange={setSoftPullExpired}
        onOpenChange={setSoftPullOpen}
      />
      <TradeSection
        addSecondTrade={addSecondTrade}
        isLoggedIn={isLoggedIn}
        isOpen={tradeOpen}
        onAddSecondTradeChange={setAddSecondTrade}
        onOpenChange={setTradeOpen}
        onTrade1Change={setTrade1}
        onTrade2Change={setTrade2}
        trade1={trade1}
        trade2={trade2}
      />
    </div>
  );
}
